import React from "react";
import { Link } from "react-router-dom";
import allRoutes from "client/constants/allRoutes";
import { meetOption } from "portal/utils/googleIntegration";

// The Google Meet mark in Google's own colours, so the feature is recognisable at a glance
// (Settings card, Appointments toolbar).
export const GoogleMeetIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 87.5 72" className={className} aria-hidden="true">
    <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z" />
    <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z" />
    <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z" />
    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z" />
    <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.13 4.85-2.37V11c0-2.53-2.94-3.93-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z" />
    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z" />
  </svg>
);

const STATE_VIEW = {
  connected: {
    label: "Connected",
    box: "border-success-500/40 bg-success-50 text-success-700 hover:bg-success-100",
    dot: "bg-success-500",
  },
  needs_reconnect: {
    label: "Reconnect",
    box: "border-warning-500/40 bg-warning-50 text-warning-700 hover:bg-warning-100",
    dot: "bg-warning-500",
  },
  disconnecting: {
    label: "Disconnecting",
    box: "border-warning-500/40 bg-warning-50 text-warning-700 hover:bg-warning-100",
    dot: "bg-warning-500",
  },
  not_connected: {
    label: "Not connected",
    box: "border-surface-border bg-surface-card text-content-secondary hover:text-content-primary hover:bg-surface-hover",
    dot: "bg-gray-300",
  },
};
const NEUTRAL_BOX = "border-surface-border bg-surface-card text-content-secondary hover:text-content-primary hover:bg-surface-hover";

/**
 * Google Calendar & Meet on the Appointments toolbar: a way to the settings page, and — green,
 * with a tick on the logo — when the clinic's Google account is connected, so the front desk
 * sees at a glance that Meet links are on. `status` is the page's shared, cached status
 * (useGoogleIntegrationStatus); null while it loads, when the button shows without a state.
 * Hidden when the feature is off on this server.
 */
export default function GoogleMeetStatusButton({ status = null }) {
  const option = status ? meetOption({ status }) : null;
  if (option && !option.visible) return null;
  const view = option ? STATE_VIEW[option.state] || null : null;
  const connected = option?.state === "connected";

  const title = !option
    ? "Google Calendar & Meet — add a Google Meet link when booking"
    : connected
      ? "Connected to Google Calendar & Meet — tick Google Meet when booking to add a Meet link"
      : option.message || "Google Calendar & Meet";

  return (
    <Link
      to={allRoutes.integrationSettings}
      title={title}
      aria-label={view ? `Google Meet: ${view.label}` : "Google Meet"}
      className={`h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border text-[11px] font-medium transition-colors ${view ? view.box : NEUTRAL_BOX}`}
    >
      <span className="relative inline-flex">
        <GoogleMeetIcon className="w-4 h-4" />
        {connected && (
          <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-success-500 ring-2 ring-white flex items-center justify-center" aria-hidden="true">
            <svg viewBox="0 0 12 12" className="w-1.5 h-1.5" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2.5 6.5 5 9 9.5 3.5" />
            </svg>
          </span>
        )}
      </span>
      <span>Google Meet</span>
      {view && (
        <>
          {!connected && <span className={`w-1.5 h-1.5 rounded-full ${view.dot}`} />}
          <span className={connected ? "font-semibold" : "text-content-tertiary"}>{view.label}</span>
        </>
      )}
    </Link>
  );
}
